import { defaultInvoiceCustomStyle } from "@components/invoice-customization/constants/defaultInvoiceCustomStyle";
import { createInvoiceStyleTokens, type InvoiceStyleTokens } from "@components/invoice-customization/utils/createInvoiceStyleTokens";
import { Box, Chip, Typography } from "@mui/material";
import { useMemo } from "react";
import { alphaVar } from "../../../../ui/colors";
import { getVisitTargetsForExtraItem } from "../../../view/utils/invoiceExtraLineItems";
import { InvoiceCalculations } from "../../shared/types";
import { formatInvoiceAppliesToNote } from "../../shared/utils";

interface ClientTotalsProps {
  calculations: InvoiceCalculations;
  serviceFeeLabel: string;
  /** Optional deposit credit applied to this invoice (shown as discount before Total Due). */
  depositApplied?: number | null;
  /** When true, hides the Subtotal/Service Fee breakdown and renders a single "Deposit" line. */
  isDeposit?: boolean;
  styleTokens?: InvoiceStyleTokens;
  invoiceDetails?: Record<string, any>;
  isPdfMode?: boolean;
}

const semantic = {
  success: {
    600: "#16A34A",
    700: "#15803D",
  },
};

const money = (value: number) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const totalRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "baseline",
  gap: 1.5,
};

const fallbackStyleTokens = createInvoiceStyleTokens(defaultInvoiceCustomStyle);

export const ClientTotals = ({ calculations, serviceFeeLabel, depositApplied, isDeposit, styleTokens, invoiceDetails, isPdfMode = false }: ClientTotalsProps) => {
  const tokens = styleTokens ?? fallbackStyleTokens;

  const labelSx = {
    color: tokens.colors.body,
    fontWeight: 650,
    fontSize: "0.82rem",
    lineHeight: 1.35,
  };

  const valueSx = {
    color: tokens.colors.total,
    fontWeight: 800,
    fontSize: "0.86rem",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums" as const,
  };

  const { subtotal = 0, serviceFeeAmount = 0, total = 0, extraLineItemsTotal = 0, discountsTotal = 0, tipAmount = 0 } = calculations || {};

  const credit = depositApplied && depositApplied > 0 ? depositApplied : 0;
  const totalDue = Math.max(0, Number(total ?? 0) + Number(tipAmount ?? 0) - credit);

  const billingEvents = useMemo(() => {
    return Array.isArray(invoiceDetails?.billingJobEvents) && invoiceDetails.billingJobEvents.length > 0
      ? invoiceDetails.billingJobEvents
      : invoiceDetails?.jobEvent
        ? [invoiceDetails.jobEvent]
        : [];
  }, [invoiceDetails?.billingJobEvents, invoiceDetails?.jobEvent]);

  const discountsList = useMemo(() => {
    const raw =
      (Array.isArray(calculations?.discounts) && calculations.discounts.length > 0 ? calculations.discounts : null) ??
      (Array.isArray(calculations?.pricingDetail?.discounts) && calculations.pricingDetail.discounts.length > 0
        ? calculations.pricingDetail.discounts
        : null) ??
      (Array.isArray(invoiceDetails?.pricingDetail?.discounts) && invoiceDetails.pricingDetail.discounts.length > 0
        ? invoiceDetails.pricingDetail.discounts
        : null) ??
      (Array.isArray(invoiceDetails?.discounts) && invoiceDetails.discounts.length > 0 ? invoiceDetails.discounts : null) ??
      [];

    return raw
      .map((item: any) => {
        const title = String(item?.description ?? item?.title ?? "").trim() || "Discount";
        const amount = Number(item?.amount ?? item?.value ?? 0);
        const note = formatInvoiceAppliesToNote(item?.appliesTo);
        return {
          title,
          note,
          amount,
        };
      })
      .filter((item: { title: string; amount: number }) => item.amount > 0);
  }, [calculations, invoiceDetails]);

  const serviceFeesList = useMemo(() => {
    const raw =
      (Array.isArray(calculations?.serviceFees) && calculations.serviceFees.length > 0 ? calculations.serviceFees : null) ??
      (Array.isArray(calculations?.pricingDetail?.serviceFees) && calculations.pricingDetail.serviceFees.length > 0
        ? calculations.pricingDetail.serviceFees
        : null) ??
      (Array.isArray(invoiceDetails?.pricingDetail?.serviceFees) && invoiceDetails.pricingDetail.serviceFees.length > 0
        ? invoiceDetails.pricingDetail.serviceFees
        : null) ??
      (Array.isArray(invoiceDetails?.serviceFees) && invoiceDetails.serviceFees.length > 0 ? invoiceDetails.serviceFees : null) ??
      [];

    return raw
      .map((item: any) => {
        const title = String(item?.alias ?? item?.label ?? item?.title ?? serviceFeeLabel ?? "Service fee").trim() || "Service fee";
        const amount = Number(item?.total?.value ?? item?.price?.value ?? item?.amount ?? 0);
        const note = formatInvoiceAppliesToNote(item?.appliesTo);
        return {
          title,
          note,
          amount,
        };
      })
      .filter((item: { title: string; amount: number }) => item.amount > 0);
  }, [calculations, invoiceDetails, serviceFeeLabel]);

  const extraLineItemsList = useMemo(() => {
    const raw =
      (Array.isArray(calculations?.extraLineItems) && calculations.extraLineItems.length > 0 ? calculations.extraLineItems : null) ??
      (Array.isArray(calculations?.pricingDetail?.extraLineItems) && calculations.pricingDetail.extraLineItems.length > 0
        ? calculations.pricingDetail.extraLineItems
        : null) ??
      (Array.isArray(invoiceDetails?.pricingDetail?.extraLineItems) && invoiceDetails.pricingDetail.extraLineItems.length > 0
        ? invoiceDetails.pricingDetail.extraLineItems
        : null) ??
      [];

    return raw
      .map((item: any) => {
        const title = String(item?.title ?? "Additional charge").trim() || "Additional charge";
        const amount = Number(item?.amount ?? 0);
        const note = item?.description ? String(item.description).trim() : undefined;
        const targetInfo = getVisitTargetsForExtraItem(item, billingEvents);
        const unitPrice = item?.unitPrice != null ? Number(item.unitPrice) : undefined;
        const qty = item?.qty != null ? Number(item.qty) : Array.isArray(item?.allocations) ? item.allocations.length : undefined;

        return {
          title,
          note,
          amount,
          targetInfo,
          unitPrice,
          qty,
        };
      })
      .filter((item: { title: string; amount: number }) => item.amount > 0);
  }, [calculations, invoiceDetails, billingEvents]);

  const rows = isDeposit
    ? [
        {
          label: "Deposit amount",
          value: money(total ?? 0),
        },
        tipAmount > 0
          ? {
              label: "Tip",
              value: money(tipAmount),
            }
          : null,
      ].filter(Boolean)
    : [
        {
          label: "Subtotal",
          value: money(subtotal),
        },
        ...(extraLineItemsList.length > 0
          ? extraLineItemsList.map((item) => ({
              label: item.title,
              note: item.note,
              targetInfo: item.targetInfo,
              unitPrice: item.unitPrice,
              qty: item.qty,
              value: `+${money(item.amount)}`,
            }))
          : extraLineItemsTotal > 0
            ? [
                {
                  label: "Additional charges",
                  value: `+${money(extraLineItemsTotal)}`,
                },
              ]
            : []),
        ...(discountsList.length > 0
          ? discountsList.map((item) => ({
              label: item.title,
              note: item.note,
              value: `-${money(item.amount)}`,
            }))
          : discountsTotal > 0
            ? [
                {
                  label: "Discounts",
                  value: `-${money(discountsTotal)}`,
                },
              ]
            : []),
        ...(serviceFeesList.length > 0
          ? serviceFeesList.map((item) => ({
              label: item.title,
              note: item.note,
              value: `+${money(item.amount)}`,
            }))
          : serviceFeeAmount > 0
            ? [
                {
                  label: serviceFeeLabel || "Service fee",
                  value: `+${money(serviceFeeAmount)}`,
                },
              ]
            : []),
        tipAmount > 0
          ? {
              label: "Tip",
              value: money(tipAmount),
            }
          : null,
      ].filter(Boolean);

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        borderRadius: 4,
        p: isPdfMode ? 1 : 1.5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: isPdfMode ? 0.75 : 1.15 }}>
        {rows.map((row: any, idx: number) => (
          <Box key={`${row.label}-${idx}`} sx={totalRowSx}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={labelSx}>{row.label}</Typography>
                {row.targetInfo?.summaryLabel && row.targetInfo.targets?.length > 0 ? (
                  <Chip
                    label={row.targetInfo.summaryLabel}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      bgcolor: row.targetInfo.isAllVisits
                        ? alphaVar(tokens.colors.total, 0.12)
                        : alphaVar(tokens.colors.body, 0.08),
                      color: row.targetInfo.isAllVisits
                        ? tokens.colors.subTitle
                        : tokens.colors.body,
                      border: `1px solid ${
                        row.targetInfo.isAllVisits
                          ? alphaVar(tokens.colors.total, 0.28)
                          : alphaVar(tokens.colors.body, 0.2)
                      }`,
                    }}
                  />
                ) : null}
              </Box>

              {row.note ? (
                <Typography
                  sx={{
                    color: tokens.colors.body,
                    fontSize: "0.72rem",
                    lineHeight: 1.35,
                    mt: 0.15,
                  }}
                >
                  {row.note}
                </Typography>
              ) : null}

              {row.unitPrice != null && row.qty != null && row.qty > 1 ? (
                <Typography
                  sx={{
                    color: tokens.colors.body,
                    fontSize: "0.72rem",
                    lineHeight: 1.35,
                    mt: 0.15,
                    fontWeight: 500,
                  }}
                >
                  {row.qty} visits × {money(row.unitPrice)}
                </Typography>
              ) : null}

              {row.targetInfo?.targets?.length > 1 && !row.targetInfo.isAllVisits ? (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.35 }}>
                  {row.targetInfo.targets.map((tgt: any) => (
                    <Typography
                      key={tgt.eventUid}
                      variant="caption"
                      sx={{
                        color: tokens.colors.body,
                        bgcolor: alphaVar(tokens.colors.body, 0.06),
                        px: 0.75,
                        py: 0.2,
                        borderRadius: 0.75,
                        fontSize: "0.68rem",
                        fontWeight: 600,
                      }}
                    >
                      {tgt.label}: {money(tgt.amount)}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Box>
            <Typography sx={valueSx}>{row.value}</Typography>
          </Box>
        ))}

        {credit > 0 ? (
          <Box sx={totalRowSx}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  ...labelSx,
                  color: semantic.success[700],
                  fontWeight: 750,
                }}
              >
                Deposit applied
              </Typography>

              <Typography
                sx={{
                  color: tokens.colors.body,
                  fontSize: "0.72rem",
                  lineHeight: 1.35,
                  mt: 0.15,
                }}
              >
                Already credited to this invoice.
              </Typography>
            </Box>

            <Typography
              sx={{
                ...valueSx,
                color: semantic.success[700],
                fontWeight: 900,
              }}
            >
              -{money(credit)}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          borderTop: tokens.borders.divider,
          mt: isPdfMode ? 1.25 : 1.75,
          pt: isPdfMode ? 1.25 : 1.75,
        }}
      >
        <Box sx={totalRowSx}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: tokens.colors.title,
                fontFamily: tokens.typography.headingFontFamily,
                fontWeight: 900,
                fontSize: "1.05rem",
                lineHeight: 1.25,
              }}
            >
              Total due
            </Typography>

            <Typography
              sx={{
                color: tokens.colors.body,
                fontSize: "0.74rem",
                lineHeight: 1.4,
                mt: 0.25,
              }}
            >
              Amount due on this invoice.
            </Typography>

            {invoiceDetails?.totalCost?.total?.currency ? (
              <Typography
                variant="caption"
                sx={{
                  color: tokens.colors.body,
                  display: "block",
                  mt: 0.35,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  fontSize: "0.7rem",
                }}
              >
                Currency: {String(invoiceDetails.totalCost.total.currency)}
              </Typography>
            ) : null}
          </Box>

          <Typography
            sx={{
              color: totalDue > 0 ? tokens.colors.total : semantic.success[700],
              fontFamily: tokens.typography.headingFontFamily,
              fontWeight: 950,
              fontSize: "1.45rem",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money(totalDue)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
